import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import axios from 'axios';
import {
  BookmarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  ShoppingCartIcon,
  PencilIcon,
  TrashIcon,
  ShareIcon,
  ClockIcon,
  TagIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  StarIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';

const OrderTemplatesPage = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  
  // State management
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    tags: [],
    shared: '',
    page: 1,
    limit: 12
  });
  
  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [expandedTemplates, setExpandedTemplates] = useState(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState(new Set());
  const [useTemplateLoading, setUseTemplateLoading] = useState(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && (typeof value !== 'object' || value.length > 0)) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
          } else {
            params.append(key, value);
          }
        }
      });
      
      const response = await axios.get(`/api/order-templates?${params}`);
      setTemplates(response.data.templates);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('שגיאה בטעינת התבניות');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  // Toggle template expansion
  const toggleTemplateExpansion = (templateId) => {
    const newExpanded = new Set(expandedTemplates);
    if (newExpanded.has(templateId)) {
      newExpanded.delete(templateId);
    } else {
      newExpanded.add(templateId);
    }
    setExpandedTemplates(newExpanded);
  };

  // Handle template selection for bulk operations
  const toggleTemplateSelection = (templateId) => {
    const newSelected = new Set(selectedTemplates);
    if (newSelected.has(templateId)) {
      newSelected.delete(templateId);
    } else {
      newSelected.add(templateId);
    }
    setSelectedTemplates(newSelected);
  };

  // Use template (add items to cart)
  const handleUseTemplate = async (templateId) => {
    try {
      setUseTemplateLoading(prev => new Set(prev).add(templateId));
      
      const response = await axios.post(`/api/order-templates/${templateId}/use`);
      
      if (response.data.success) {
        // Add items to cart
        for (const item of response.data.preparedItems) {
          await addToCart(item.product, item.quantity);
        }
        
        // Show success message
        const totalItems = response.data.preparedItems.length;
        const unavailableItems = response.data.unavailableItems.length;
        
        let message = `${totalItems} פריטים נוספו לעגלה`;
        if (unavailableItems > 0) {
          message += ` (${unavailableItems} פריטים לא זמינים)`;
        }
        
        window.alert(message);
        
        // Refresh templates to update usage count
        fetchTemplates();
      }
    } catch (err) {
      console.error('Use template error:', err);
      window.alert('שגיאה בהוספת פריטים לעגלה');
    } finally {
      setUseTemplateLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(templateId);
        return newSet;
      });
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק תבנית זו?')) return;
    
    try {
      await axios.delete(`/api/order-templates/${templateId}`);
      window.alert('התבנית נמחקה בהצלחה');
      fetchTemplates();
    } catch (err) {
      console.error('Delete template error:', err);
      window.alert('שגיאה במחיקת התבנית');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get all unique tags from templates
  const getAllTags = () => {
    const allTags = new Set();
    templates.forEach(template => {
      if (template.tags && Array.isArray(template.tags)) {
        template.tags.forEach(tag => allTags.add(tag));
      }
    });
    return Array.from(allTags);
  };

  if (loading && templates.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BookmarkIcon className="h-8 w-8 ml-3 text-blue-600" />
                תבניות הזמנה
              </h1>
              <p className="mt-2 text-gray-600">
                שמור תבניות הזמנה לרכישה מהירה וחוזרת
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex items-center space-x-4 space-x-reverse">
              {selectedTemplates.size > 0 && (
                <div className="flex items-center space-x-2 space-x-reverse">
                  <span className="text-sm text-gray-600">
                    {selectedTemplates.size} תבניות נבחרו
                  </span>
                  <button
                    onClick={() => {/* Bulk operations */}}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
              >
                <PlusIcon className="h-4 w-4 ml-2" />
                תבנית חדשה
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-gray-700 hover:text-gray-900"
            >
              <FunnelIcon className="h-5 w-5 ml-2" />
              סינון וחיפוש
              {showFilters ? (
                <ChevronUpIcon className="h-4 w-4 mr-2" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 mr-2" />
              )}
            </button>
          </div>
          
          {showFilters && (
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    חיפוש תבנית
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute right-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="שם תבנית, תיאור..."
                      className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Shared Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    סוג תבנית
                  </label>
                  <select
                    value={filters.shared}
                    onChange={(e) => handleFilterChange('shared', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">כל התבניות</option>
                    <option value="false">התבניות שלי</option>
                    <option value="true">תבניות משותפות</option>
                  </select>
                </div>

                {/* Tags Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תגיות
                  </label>
                  <select
                    multiple
                    value={filters.tags}
                    onChange={(e) => handleFilterChange('tags', Array.from(e.target.selectedOptions, option => option.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {getAllTags().map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <div className="mr-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Templates Grid */}
        {templates.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <BookmarkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              לא נמצאו תבניות
            </h3>
            <p className="text-gray-600 mb-4">
              {filters.search || filters.tags.length > 0
                ? 'נסה לשנות את הפילטרים'
                : 'צור תבנית ראשונה כדי להתחיל'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              צור תבנית חדשה
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div key={template.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                {/* Template Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedTemplates.has(template.id)}
                          onChange={() => toggleTemplateSelection(template.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ml-3"
                        />
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          {template.is_shared ? (
                            <ShareIcon className="h-4 w-4 text-blue-500 ml-2" />
                          ) : (
                            <BookmarkSolidIcon className="h-4 w-4 text-yellow-500 ml-2" />
                          )}
                          {template.name}
                        </h3>
                      </div>
                      
                      {template.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {template.description}
                        </p>
                      )}

                      {/* Template Stats */}
                      <div className="flex items-center space-x-4 space-x-reverse text-xs text-gray-500 mt-2">
                        <span className="flex items-center">
                          <TagIcon className="h-3 w-3 ml-1" />
                          {template.stats?.uniqueProducts || 0} מוצרים
                        </span>
                        <span className="flex items-center">
                          <ClockIcon className="h-3 w-3 ml-1" />
                          {template.usage_count || 0} שימושים
                        </span>
                        {template.stats?.unavailableItems > 0 && (
                          <span className="text-red-500">
                            {template.stats.unavailableItems} לא זמינים
                          </span>
                        )}
                      </div>

                      {/* Tags */}
                      {template.tags && template.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {template.tags.slice(0, 3).map(tag => (
                            <span
                              key={tag}
                              className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {template.tags.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{template.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Template Actions */}
                    <div className="flex items-center space-x-1 space-x-reverse">
                      {template.user_id === user?.id && (
                        <>
                          <button
                            onClick={() => {/* Edit template */}}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title="ערוך תבנית"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="מחק תבנית"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => toggleTemplateExpansion(template.id)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        {expandedTemplates.has(template.id) ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Template Items (Expanded) */}
                {expandedTemplates.has(template.id) && (
                  <div className="p-6 border-b border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">פריטים בתבנית</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {template.items.map((item) => (
                        <div key={item.id} className="flex items-center space-x-3 space-x-reverse text-sm">
                          {/* Product Image */}
                          <div className="flex-shrink-0">
                            {item.product?.images && item.product.images.length > 0 ? (
                              <img
                                src={item.product.images[0]}
                                alt={item.product.name}
                                className="h-8 w-8 rounded object-cover"
                              />
                            ) : (
                              <div className="h-8 w-8 bg-gray-200 rounded flex items-center justify-center">
                                <TagIcon className="h-4 w-4 text-gray-400" />
                              </div>
                            )}
                          </div>
                          
                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${
                              item.product?.is_active ? 'text-gray-900' : 'text-gray-400 line-through'
                            }`}>
                              {item.product?.name || 'מוצר לא זמין'}
                            </p>
                            <p className="text-gray-500 text-xs">
                              SKU: {item.product?.sku || 'N/A'}
                            </p>
                          </div>
                          
                          {/* Quantity */}
                          <div className="text-left">
                            <span className="font-medium">{item.quantity}</span>
                            {item.is_optional && (
                              <span className="text-xs text-blue-500 block">אופציונלי</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Template Footer */}
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      <div>נוצר: {formatDate(template.createdAt)}</div>
                      {template.last_used && (
                        <div>שימוש אחרון: {formatDate(template.last_used)}</div>
                      )}
                      {template.user && template.user.id !== user?.id && (
                        <div>נוצר על ידי: {template.user.first_name} {template.user.last_name}</div>
                      )}
                    </div>

                    <button
                      onClick={() => handleUseTemplate(template.id)}
                      disabled={useTemplateLoading.has(template.id) || !template.stats?.canOrder}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
                    >
                      {useTemplateLoading.has(template.id) ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
                      ) : (
                        <ShoppingCartIcon className="h-4 w-4 ml-2" />
                      )}
                      הוסף לעגלה
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-8 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              מציג {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} - {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} מתוך {pagination.totalItems} תבניות
            </div>
            <div className="flex space-x-1 space-x-reverse">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                הקודם
              </button>
              
              {[...Array(pagination.totalPages)].map((_, index) => {
                const page = index + 1;
                const isCurrentPage = page === pagination.currentPage;
                
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      isCurrentPage
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                הבא
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTemplatesPage;
